import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService, IUtilsDictionary, LoggingService, UtilsDate, UtilsJson, UtilsMisc, UtilsString } from '@pit/core';
import { EntityDataService } from '@pit/core-data';
import { DtoShipment, DtoShipmentList, DtoStatsDepotShipments, IDtoShipment, IDtoShipmentList, IDtoStatsDepotShipments } from '@pit/frontend-model';
import { map, Observable } from 'rxjs';
import { IDtoShipmentEntityDataService } from '../interfaces/dto.shipment.entity.data.service.interface';

@Injectable({
  providedIn: 'root'
})
export class DtoShipmentEntityDataService extends EntityDataService<IDtoShipment> implements IDtoShipmentEntityDataService {

  constructor(http: HttpClient, configService: ConfigService, loggingService: LoggingService) {
    super(http, null, configService, loggingService);
  }

  public getShipmentByShipmentNumber$(shipmentNumber: string): Observable<IDtoShipment> {
    return this.httpGet(`${this.apiUrl}/${shipmentNumber}`)
      .pipe(map((shipment: IDtoShipment) => {
        if (UtilsMisc.isNullOrUndefined(shipment) === false) {
          return this.createEntityFromJson(shipment);
        } else {
          return null;
        }
      }));
  }

  public async getShipmentByShipmentNumberAsync(shipmentNumber: string): Promise<IDtoShipment> {
    return (await this.getShipmentByShipmentNumber$(shipmentNumber).toPromise());
  }

  public getShipmentByShipmentNumberForMandator$(mandator: string, shipmentNumber: string): Observable<IDtoShipment> {
    return this.httpGet(`${this.apiUrl}/mandator/${mandator}/${shipmentNumber}`)
      .pipe(map((shipment: IDtoShipment) => {
        if (UtilsMisc.isNullOrUndefined(shipment) === false) {
          return this.createEntityFromJson(shipment);
        } else {
          return null;
        }
      }));

  }

  public async getShipmentByShipmentNumberForMandatorAsync(mandator: string, shipmentNumber: string): Promise<IDtoShipment> {
    return (await this.getShipmentByShipmentNumberForMandator$(mandator, shipmentNumber).toPromise());
  }

  public getShipmentsForDate$(shipmentDate: Date): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/shipmentDate/${shipmentDate}`)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentsForDateAsync(shipmentDate: Date): Promise<IDtoShipment[]> {
    return (await this.getShipmentsForDate$(shipmentDate).toPromise());
  }

  public getShipmentsForDevice$(deviceId: string): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/device/${deviceId}`)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentsForDeviceAsync(deviceId: string): Promise<IDtoShipment[]> {
    return (await this.getShipmentsForDevice$(deviceId).toPromise());
  }

  public removeShipmentsFromDevice$(deviceId: string, shipmentIds: string[]): Observable<boolean> {
    return this.httpPost(`${this.apiUrl}/remove/device/${deviceId}`, shipmentIds)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async removeShipmentsFromDeviceAsync(deviceId: string, shipmentIds: string[]): Promise<boolean> {
    return (await this.removeShipmentsFromDevice$(deviceId, shipmentIds).toPromise());
  }

  public removeAllShipmentsFromDevice$(deviceId: string): Observable<boolean> {
    const urlPart: string = UtilsString.stringFormat('{0}/all', deviceId);
    return this.httpPost(`${this.apiUrl}/remove/device/${deviceId}/all`, null)
      .pipe(map((json: boolean) => {
        return json;
      }));
  }

  public async removeAllShipmentsFromDeviceAsync(deviceId: string): Promise<boolean> {
    return (await this.removeAllShipmentsFromDevice$(deviceId).toPromise());
  }

  public getShipmentsToBookToDepot$(numberDepot: string): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/depot/book/${numberDepot}`)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentsToBookToDepotAsync(numberDepot: string): Promise<IDtoShipment[]> {
    return (await this.getShipmentsToBookToDepot$(numberDepot).toPromise());
  }

  public getActiveShipmentsForUser$(userName: string): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/active/user/${userName}`, null, 30000)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getActiveShipmentsForUserAsync(userName: string): Promise<IDtoShipment[]> {
    return (await this.getActiveShipmentsForUser$(userName).toPromise());
  }

  public getActiveShipmentsForDepot$(depotNumber: string): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/active/depot/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getActiveShipmentsForDepotAsync(depotNumber: string): Promise<IDtoShipment[]> {
    return (await this.getActiveShipmentsForDepot$(depotNumber).toPromise());
  }

  public getShipmentsToReturnForDepot$(depotNumber: string, includeInDelivery: boolean = false): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/returns/depot/${depotNumber}/${includeInDelivery.toString()}`, null, 30000)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentsToReturnForDepotAsync(depotNumber: string, includeInDelivery: boolean = false): Promise<IDtoShipment[]> {
    return (await this.getShipmentsToReturnForDepot$(depotNumber, includeInDelivery).toPromise());
  }

  public getShipmentsToReturnForDevice$(deviceId: string): Observable<IDtoShipment[]> {
    return this.httpGet(`${this.apiUrl}/returns/device/${deviceId}`, null, 30000)
      .pipe(map((entities: IDtoShipment[]) => {
        const result: IDtoShipment[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createEntityFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentsToReturnForDeviceAsync(deviceId: string): Promise<IDtoShipment[]> {
    return (await this.getShipmentsToReturnForDevice$(deviceId).toPromise());
  }

  public getShipmentListItemsActiveForDepot$(depotNumber: string): Observable<IDtoShipmentList[]> {
    return this.httpGet(`${this.apiUrl}/list/depot/active/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoShipmentList[]) => {
        const result: IDtoShipmentList[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createDtoShipmentListFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentListItemsActiveForDepotAsync(depotNumber: string): Promise<IDtoShipmentList[]> {
    return (await this.getShipmentListItemsActiveForDepot$(depotNumber).toPromise());
  }

  public getShipmentListItemsReturnsForDepot$(depotNumber: string, includeInDelivery: boolean = false): Observable<IDtoShipmentList[]> {
    return this.httpGet(`${this.apiUrl}/list/depot/returns/${depotNumber}/${includeInDelivery.toString()}`, null, 30000)
      .pipe(map((entities: IDtoShipmentList[]) => {
        const result: IDtoShipmentList[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createDtoShipmentListFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentListItemsReturnsForDepotAsync(depotNumber: string, includeInDelivery: boolean = false): Promise<IDtoShipmentList[]> {
    return (await this.getShipmentListItemsReturnsForDepot$(depotNumber, includeInDelivery).toPromise());
  }

  public getShipmentListItemsLostForDepot$(depotNumber: string): Observable<IDtoShipmentList[]> {
    return this.httpGet(`${this.apiUrl}/list/lost/depot/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoShipmentList[]) => {
        const result: IDtoShipmentList[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createDtoShipmentListFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getShipmentListItemsLostForDepotAsync(depotNumber: string): Promise<IDtoShipmentList[]> {
    return (await this.getShipmentListItemsLostForDepot$(depotNumber).toPromise());
  }

  public getDepotShipmentsStatsForMandator$(mandator: string, depotNumber: string): Observable<IDtoStatsDepotShipments[]> {
    return this.httpGet(`${this.apiUrl}/stats/mandator/${mandator}/depot/${depotNumber}`, null, 30000)
      .pipe(map((entities: IDtoStatsDepotShipments[]) => {
        const result: IDtoStatsDepotShipments[] = [];
        if ((UtilsMisc.isNullOrUndefined(entities) === false) && (entities.length > 0)) {
          for (const entity of entities) {
            result.push(this.createDtoStatsDepotShipmentsFromJson(entity));
          }
        }
        return result;
      }));
  }

  public async getDepotShipmentsStatsForMandatorAsync(mandator: string, depotNumber: string): Promise<IDtoStatsDepotShipments[]> {
    return (await this.getDepotShipmentsStatsForMandator$(mandator, depotNumber).toPromise());
  }

  protected override doGetApiUrl(): string {
    return `/api/web/shipments`
  }

  protected override createEntityFromJson(json: any): IDtoShipment {
    const result: IDtoShipment = (UtilsJson.deserialize(json, DtoShipment) as IDtoShipment);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

  protected override doGetUrls(): IUtilsDictionary<string> {
    this._urls = super.doGetUrls();

    this._urls.add('get.by.number', UtilsString.stringFormat('{0}://{1}:{2}/{3}/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.by.number.mandator', UtilsString.stringFormat('{0}://{1}:{2}/{3}/mandator/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}/{1}');
    this._urls.add('get.by.date', UtilsString.stringFormat('{0}://{1}:{2}/{3}/shipmentDate/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.by.device', UtilsString.stringFormat('{0}://{1}:{2}/{3}/device/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.to.book.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/depot/book/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.active.by.user', UtilsString.stringFormat('{0}://{1}:{2}/{3}/active/user/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.active.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/active/depot/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.returns.by.device', UtilsString.stringFormat('{0}://{1}:{2}/{3}/returns/device/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.returns.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/returns/depot/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}/{1}');
    this._urls.add('get.list.items.active.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/list/depot/active/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.list.items.returns.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/list/depot/returns/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}/{1}');
    this._urls.add('get.list.items.lost.by.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/list/lost/depot/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('get.stats.depot', UtilsString.stringFormat('{0}://{1}:{2}/{3}/stats/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + 'mandator/{0}/depot/{1}');
    this._urls.add('remove.from.device', UtilsString.stringFormat('{0}://{1}:{2}/{3}/remove/device/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');
    this._urls.add('remove.all.from.device', UtilsString.stringFormat('{0}://{1}:{2}/{3}/remove/device/', this.configurationService.configData.protocol, this.configurationService.configData.ip, this.configurationService.configData.port, this.apiUrl) + '{0}');

    return this._urls;
  }

  private createDtoShipmentListFromJson(json: any): IDtoShipmentList {
    const result: IDtoShipmentList = (UtilsJson.deserialize(json, DtoShipmentList) as IDtoShipmentList);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

  private createDtoStatsDepotShipmentsFromJson(json: any): IDtoStatsDepotShipments {
    const result: IDtoStatsDepotShipments = (UtilsJson.deserialize(json, DtoStatsDepotShipments) as IDtoStatsDepotShipments);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

}
